import { isRejectedWithValue, Middleware, MiddlewareAPI } from "@reduxjs/toolkit";
// eslint-disable-next-line no-restricted-imports -- not exported from @reduxjs/toolkit directly
import type { UnknownAsyncThunkRejectedAction } from "@reduxjs/toolkit/dist/matchers";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { addErrorResponse } from "../slices/errorResponseSlice";
import { ErrorInstance, selectIsErrorInstanceSuppressed } from "../slices/errorSuppressionSlice";
import { Advise, AdviseErrorResponseData, AdviseType, ErrorResponseType } from "./errorResponseTypes";

// RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
export const errorResponseMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRtkQueryRejectedAction(action)) {
    // check, if the error instance is suppressed
    const errorInstance = createErrorInstance(action);
    if (!selectIsErrorInstanceSuppressed(errorInstance)(api.getState())) {
      // first, handle explicit advise errors from the backend
      if (isAdviseErrorResponse(action.payload.data)) {
        next(addErrorResponse({
          type: ErrorResponseType.ADVISE,
          data: action.payload.data,
        }));

        return next(action);
      }

      // if not an advise error, handle the following error types separately
      // -> timeout
      if (action.payload.status === "TIMEOUT_ERROR" || action.payload.status === 503) {
        next(addErrorResponse({
          type: ErrorResponseType.TIMEOUT,
        }));

        return next(action);
      }
      // -> payload too large
      if (action.payload.status === 413) {
        next(addErrorResponse({
          type: ErrorResponseType.PAYLOAD_TOO_LARGE,
        }));

        return next(action);
      }

      // all other error types are unknown to us
      next(addErrorResponse({
        type: ErrorResponseType.UNKNOWN,
      }));
    }
  }

  return next(action);
};

const createErrorInstance = (action: RtkQueryRejectedAction): ErrorInstance => {
  return {
    apiName: action.type.split("/")[0],
    endpointName: action.meta.arg.endpointName,
    status: action.payload.status,
  };
};

interface RtkQueryRejectedAction {
  error: UnknownAsyncThunkRejectedAction["error"];
  meta: RtkQueryRejectedActionMeta;
  payload: FetchBaseQueryError;
  type: string;
}

type RtkQueryRejectedActionMeta = UnknownAsyncThunkRejectedAction["meta"] & {
  arg: Record<"endpointName", string>;
};

const isRtkQueryRejectedAction = (action: unknown): action is RtkQueryRejectedAction => {
  if (!isRejectedWithValue(action)) {
    return false;
  }

  const typedAction = action as Partial<RtkQueryRejectedAction>;
  return isRtkQueryRejectedActionMeta(typedAction.meta)
    && isFetchBaseQueryError(typedAction.payload);
};

const isRtkQueryRejectedActionMeta = (meta: unknown): meta is RtkQueryRejectedActionMeta => {
  if (typeof meta !== "object" || meta == null) {
    return false;
  }

  const typedMeta = meta as UnknownAsyncThunkRejectedAction["meta"];
  if (!("arg" in typedMeta) || typeof typedMeta.arg !== "object" || typedMeta.arg == null) {
    return false;
  }

  const typedArg = typedMeta.arg as RtkQueryRejectedActionMeta["arg"];
  return "endpointName" in typedArg && typeof typedArg.endpointName === "string";
};

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError => {
  return typeof error === "object" && error != null && "status" in error;
};

const isAdviseErrorResponse = (adviseErrorResponse: unknown): adviseErrorResponse is AdviseErrorResponseData => {
  if (!adviseErrorResponse || typeof adviseErrorResponse !== "object") {
    return false;
  }

  const typedErrorAdvise = adviseErrorResponse as Partial<AdviseErrorResponseData>;
  return typeof typedErrorAdvise.type === "string"
    && typeof typedErrorAdvise.title === "string"
    && typeof typedErrorAdvise.status === "number"
    && typeof typedErrorAdvise.instance === "string"
    && isAdvise(typedErrorAdvise.advise);
};


const isAdvise = (advise: unknown): advise is Advise => {
  if (!advise || typeof advise !== "object") {
    return false;
  }

  type RequiredFieldNames = "type" | "timestamp";
  type RequiredFields = Pick<Advise, RequiredFieldNames>;
  type OptionalFields = Exclude<keyof Advise, RequiredFieldNames>;
  type MaybeAdvise = Partial<RequiredFields> & Record<OptionalFields, unknown>;
  const typedAdvise = advise as MaybeAdvise;

  return (
    // check required fields first
    typeof typedAdvise.type === "string" && Object.values(AdviseType).includes(typedAdvise.type)
    && typeof typedAdvise.timestamp === "string"

    // then check optional fields (are allowed to be absent)
    && (!typedAdvise.uuid || typeof typedAdvise.uuid === "string")
    && (!typedAdvise.messages || isMessages(typedAdvise.messages))
    && (!typedAdvise.validationViolations || isValidationViolations(typedAdvise.validationViolations))
  );
};

const isMessages = (messages: unknown): messages is Record<string, string> => {
  if (!messages || typeof messages !== "object") {
    return false;
  }

  return Object.values(messages).every((value: unknown) => typeof value === "string");
};

const isValidationViolations = (validationViolations: unknown): validationViolations is Record<string, string[]> => {
  if (!validationViolations || typeof validationViolations !== "object") {
    return false;
  }

  const isStringArrPredicate = (arr: unknown) => Array.isArray(arr) && arr.every((s) => typeof s === "string");
  return Object.values(validationViolations).every(isStringArrPredicate);
};
